import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useStandardDialog } from 'src/careflow/ph-core/ui/base/StandardDialogProvider';
import { useStandardLoadingBackdrop } from 'src/careflow/ph-core/ui/base/StandardLoadingBackdropProvider';
import FuegoAppEmbeddedDialog from './FuegoAppEmbeddedDialog';
import { generatePayloadForContext } from './services/fuegoBundleService';
import { FuegoFlowAction, FuegoFlowContext, FuegoFlowTarget } from './types';

export type { FuegoFlowAction, FuegoFlowTarget, FuegoFlowContext };

export function useFuegoApp() {
  const standardDialog = useStandardDialog();
  const standardLoadingBackdrop = useStandardLoadingBackdrop();
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setIsGenerating] = useState(false);

  const startFlow = useCallback(
    async (context: FuegoFlowContext) => {
      console.debug('[useFuegoApp] Starting flow with context:', context);

      let payload = context.preloadedPayload;

      // Generate the FHIR bundles BEFORE launching the Fuego App dialog
      if (!payload) {
        try {
          setIsGenerating(true);
          standardLoadingBackdrop.open();

          payload = await generatePayloadForContext(context);
        } catch (err: any) {
          console.error(
            '[useFuegoApp] Error generating FHIR bundles before launching Fuego App:',
            err
          );
          enqueueSnackbar(err?.message || 'Failed to assemble FHIR bundles for Fuego App', {
            variant: 'error',
          });
          return;
        } finally {
          standardLoadingBackdrop.close();
          setIsGenerating(false);
        }
      }

      const contextWithPayload: FuegoFlowContext = {
        ...context,
        preloadedPayload: payload,
      };

      // Dispatch custom window event so any parent listener or bridge can intercept
      if (typeof window !== 'undefined') {
        const flowEvent = new CustomEvent('fuego-flow-start', {
          detail: contextWithPayload,
        });
        window.dispatchEvent(flowEvent);
      }

      // Render the embedded Fuego App dialog inside the standard dialog with the preloaded payload
      standardDialog.open(
        <FuegoAppEmbeddedDialog context={contextWithPayload} onClose={standardDialog.close} />,
        'xl'
      );
    },
    [enqueueSnackbar, standardDialog, standardLoadingBackdrop]
  );

  const viewOnFuego = useCallback(
    (context: Omit<FuegoFlowContext, 'action'>) => {
      startFlow({
        ...context,
        action: 'view',
      });
    },
    [startFlow]
  );

  const mergeOnFuego = useCallback(
    (context: Omit<FuegoFlowContext, 'action'>) => {
      startFlow({
        ...context,
        action: 'merge',
      });
    },
    [startFlow]
  );

  const mergeLinkedPatient = useCallback(
    (params: {
      primaryPatientId: string;
      targetPatientId: string;
      currentPatientName?: string | null;
      targetPatientName?: string | null;
      options?: Record<string, any>;
    }) => {
      startFlow({
        action: 'merge',
        target: 'patient',
        primaryPatientId: params.primaryPatientId,
        targetPatientId: params.targetPatientId,
        currentPatientName: params.currentPatientName,
        targetPatientName: params.targetPatientName,
        options: {
          lockPrimaryRelatedResourcesOnMerge: true,
          keepOriginalResourceIdsOnMerge: true,
          keepDataNotSelectableOnMerge: true,
          ...params.options,
        },
      });
    },
    [startFlow]
  );

  const mergeDocumentAttachment = useCallback(
    (params: {
      primaryPatientId: string;
      documentReference: any;
      currentPatientName?: string | null;
      options?: Record<string, any>;
    }) => {
      startFlow({
        action: 'merge',
        target: 'documentReference',
        primaryPatientId: params.primaryPatientId,
        documentReference: params.documentReference,
        currentPatientName: params.currentPatientName,
        options: {
          lockPrimaryRelatedResourcesOnMerge: true,
          keepOriginalResourceIdsOnMerge: true,
          keepDataNotSelectableOnMerge: true,
          ...params.options,
        },
      });
    },
    [startFlow]
  );

  return {
    startFlow,
    viewOnFuego,
    mergeOnFuego,
    mergeLinkedPatient,
    mergeDocumentAttachment,
    closeDialog: standardDialog.close,
    isGenerating,
  };
}

export default useFuegoApp;
